import React, { useState, useEffect } from 'react';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import SupervisorBackLink from '../components/SupervisorBackLink.jsx';
import CoachQuestionsBank, { COACH_QUESTION_CATEGORIES } from '../components/CoachQuestionsBank.jsx';
import CoachManadaView from '../components/CoachManadaView.jsx';
import { diagnosticoAPI } from '../services/api';
import { normalizeQuestionsBank } from '../utils/coachQuestionsBank.js';
import './Coach.css';
import '../components/CoachManadaView.css';

export default function Coach() {
    const { user } = useAuth();
    const role = user?.rol || user?.role;
    const isSupervisor = role === 'supervisor';
    const isAdmin = role === 'admin' || role === 'superadmin';

    const [tab, setTab] = useState('curriculum');
    const [guides, setGuides] = useState([]);
    const [questions, setQuestions] = useState(null);
    const [curriculum, setCurriculum] = useState(null);
    const [improvementPlan, setImprovementPlan] = useState(null);
    const [supervisorPlans, setSupervisorPlans] = useState([]);
    const [selectedSupervisorId, setSelectedSupervisorId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);
    const [teamData, setTeamData] = useState([]);
    const [guidesLoaded, setGuidesLoaded] = useState(false);
    const [manadaEmployees, setManadaEmployees] = useState([]);
    const [guidesLeaders, setGuidesLeaders] = useState([]);
    const [selectedGuideLeaderId, setSelectedGuideLeaderId] = useState(null);
    const [leaderPlanPreview, setLeaderPlanPreview] = useState(null);
    const [leaderPlanPreviewLoading, setLeaderPlanPreviewLoading] = useState(false);

    const { tenantId } = useTenantScope();

    useEffect(() => {
        loadCore();
    }, [tenantId]);

    useEffect(() => {
        if (tab === 'curriculum' && !curriculum) loadCurriculum();
        if (tab === 'plan') loadPlan(selectedSupervisorId);
        if (tab === 'questions' && !questions) loadQuestions();
        if (tab === 'guides' && !guidesLoaded) loadGuidesContext();
    }, [tab, tenantId]);

    async function loadGuidesContext() {
        setTabLoading(true);
        try {
            if (isAdmin) {
                const [plansRes, manadaRes] = await Promise.all([
                    api.getCoachImprovementPlans(),
                    api.getMapaManada(),
                ]);
                const list = plansRes?.data?.supervisors || plansRes?.supervisors || [];
                setGuidesLeaders(list);
                setManadaEmployees(manadaRes?.data?.employees || manadaRes?.employees || []);
                if (list.length > 0) {
                    const firstId = list[0].supervisorId;
                    setSelectedGuideLeaderId(firstId);
                    await loadLeaderPlanPreview(firstId);
                }
            } else if (isSupervisor && user?.id) {
                const teamRes = await diagnosticoAPI.getEquipo(user.id);
                setTeamData(teamRes.data?.data || teamRes?.data || []);
            }
            setGuidesLoaded(true);
        } catch (err) {
            console.error('guides context:', err);
        } finally {
            setTabLoading(false);
        }
    }

    async function loadLeaderPlanPreview(supervisorId) {
        if (!supervisorId) return;
        setSelectedGuideLeaderId(supervisorId);
        setLeaderPlanPreviewLoading(true);
        try {
            const res = await api.getCoachImprovementPlan(supervisorId);
            setLeaderPlanPreview(res?.data || res);
        } catch {
            setLeaderPlanPreview(null);
        } finally {
            setLeaderPlanPreviewLoading(false);
        }
    }

    function handleOpenFromManada(target) {
        if (target === 'questions') {
            setTab('questions');
            if (!questions) loadQuestions();
            return;
        }
        if (typeof target === 'number' || (typeof target === 'string' && target)) {
            const id = Number(target);
            setSelectedSupervisorId(id);
            setTab('plan');
            loadPlan(id);
            return;
        }
        setTab('plan');
        loadPlan(isSupervisor ? undefined : selectedSupervisorId);
    }

    async function loadCore() {
        setLoading(true);
        try {
            const guidesRes = await api.getCoachingGuides();
            setGuides(guidesRes?.data || guidesRes || []);
        } catch (err) {
            console.error('Error loading coach data:', err);
            setGuides([]);
        } finally {
            setLoading(false);
        }
    }

    async function loadQuestions() {
        setTabLoading(true);
        try {
            const res = await api.getPowerfulQuestions();
            setQuestions(normalizeQuestionsBank(res) || res?.data || res);
        } catch (err) {
            console.error(err);
            setQuestions(null);
        } finally {
            setTabLoading(false);
        }
    }

    async function loadCurriculum() {
        setTabLoading(true);
        try {
            const res = await api.getCoachTrainingCurriculum();
            setCurriculum(res?.data || res);
        } catch (err) {
            console.error(err);
            setCurriculum(null);
        } finally {
            setTabLoading(false);
        }
    }

    async function loadPlan(supervisorId) {
        setTabLoading(true);
        try {
            if (isAdmin && !supervisorId) {
                const listRes = await api.getCoachImprovementPlans();
                const list = listRes?.data?.supervisors || listRes?.supervisors || [];
                setSupervisorPlans(list);
                if (list.length > 0) {
                    const firstId = list[0].supervisorId;
                    setSelectedSupervisorId(firstId);
                    const planRes = await api.getCoachImprovementPlan(firstId);
                    setImprovementPlan(planRes?.data || planRes);
                } else {
                    setImprovementPlan(null);
                }
            } else {
                const planRes = await api.getCoachImprovementPlan(
                    isAdmin && supervisorId ? supervisorId : undefined
                );
                setImprovementPlan(planRes?.data || planRes);
            }
        } catch (err) {
            console.error(err);
            setImprovementPlan(null);
        } finally {
            setTabLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="spinner" />
            </div>
        );
    }

    const frictionBadge = (level) => {
        const colors = { green: 'var(--color-alert-green)', yellow: 'var(--color-alert-yellow)', red: 'var(--color-alert-red)' };
        return (
            <span className="coach-friction-badge" style={{ backgroundColor: colors[level] || '#94a3b8' }}>
                {level === 'red' ? 'Prioridad' : level === 'yellow' ? 'Atención' : 'Estable'}
            </span>
        );
    };

    return (
        <div className="container container-wide animate-in">
            {isSupervisor && <SupervisorBackLink />}
            <h2>🧭 Plan de Coach — El Arriero Digital</h2>
            <p className="text-muted mb-lg">
                Temario por perfiles de la manada, banco maestro de preguntas y planes de mejoramiento según el
                Observador. IA opcional vía Ollama local (OLLAMA_BASE_URL).
            </p>

            <div
                className="coach-disclaimer glass-card"
                style={{
                    borderLeft: '4px solid var(--color-alert-yellow)',
                    backgroundColor: 'var(--color-surface)',
                    marginBottom: '1.5rem',
                }}
            >
                <p className="text-sm" style={{ margin: 0 }}>
                    ⚠️ <strong>Principios éticos:</strong> Herramientas para comprender, no para diagnosticar. Brújula
                    operativa, no evaluación automática de desempeño.
                </p>
            </div>

            <>
                    <div className="tabs mb-lg coach-tabs">
                        <button
                            type="button"
                            className={`btn ${tab === 'curriculum' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setTab('curriculum')}
                        >
                            📚 Temario formación
                        </button>
                        <button
                            type="button"
                            className={`btn ${tab === 'plan' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setTab('plan')}
                        >
                            📋 Plan mejoramiento
                        </button>
                        <button
                            type="button"
                            className={`btn ${tab === 'guides' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setTab('guides')}
                        >
                            📖 Comprensión manada
                        </button>
                        <button
                            type="button"
                            className={`btn ${tab === 'questions' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setTab('questions')}
                        >
                            ❓ Banco preguntas
                        </button>
                    </div>

                    {tabLoading && (
                        <p className="text-muted mb-md">
                            <span className="spinner-inline" /> Cargando…
                        </p>
                    )}

                    {tab === 'curriculum' && curriculum && (
                        <div className="coach-content stagger">
                            <div className="glass-card mb-lg">
                                <h3 style={{ marginTop: 0 }}>Mix de perfiles — {curriculum.scope === 'organization' ? 'Empresa' : 'Tu manada'}</h3>
                                <p className="text-muted">{curriculum.observadorAlignment?.message}</p>
                                <div className="coach-profile-mix">
                                    {Object.entries(curriculum.profileMix || {}).map(([k, v]) => (
                                        <div key={k} className="coach-mix-chip">
                                            <strong>{COACH_QUESTION_CATEGORIES[k] || k}</strong>
                                            <span>
                                                {v.count} ({v.percent}%)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                {curriculum.narrative && (
                                    <p className="coach-ai-note">
                                        {curriculum.aiAvailable ? '🤖 ' : ''}
                                        {curriculum.narrative}
                                    </p>
                                )}
                            </div>
                            {curriculum.modules?.map((mod) => (
                                <div key={mod.id} className="glass-card coach-module-card mb-md">
                                    <h4 style={{ marginTop: 0 }}>{mod.title}</h4>
                                    {mod.sharePercent != null && (
                                        <p className="text-sm text-muted">
                                            {mod.headcount} personas · {mod.sharePercent}% de la manada
                                        </p>
                                    )}
                                    <ul className="coach-list ml-md">
                                        {(mod.objectives || []).map((o, i) => (
                                            <li key={i}>{o}</li>
                                        ))}
                                    </ul>
                                    <h5>Sesiones sugeridas</h5>
                                    <ul className="coach-list ml-md text-sm">
                                        {(mod.sessions || []).map((s, i) => (
                                            <li key={i}>
                                                Semana {s.week}: <strong>{s.topic}</strong> — {s.activity}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === 'plan' && improvementPlan && (
                        <div className="coach-content stagger">
                            {isAdmin && supervisorPlans.length > 0 && (
                                <div className="glass-card mb-lg">
                                    <label className="text-sm">
                                        Encargado / supervisor
                                        <select
                                            className="coach-select"
                                            value={selectedSupervisorId || ''}
                                            onChange={(e) => {
                                                const id = Number(e.target.value);
                                                setSelectedSupervisorId(id);
                                                loadPlan(id);
                                            }}
                                        >
                                            {supervisorPlans.map((s) => (
                                                <option key={s.supervisorId} value={s.supervisorId}>
                                                    {s.supervisorName} — {s.level} (riesgo {s.avgRisk})
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            )}
                            <div className="glass-card mb-lg" style={{ borderTop: '6px solid var(--color-primary)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                                    <h3 style={{ margin: 0 }}>{improvementPlan.supervisorName}</h3>
                                    {frictionBadge(improvementPlan.frictionSummary?.level)}
                                </div>
                                <p className="text-sm text-muted mt-sm">{improvementPlan.observadorSource}</p>
                                {improvementPlan.narrative && (
                                    <p className="coach-ai-note">{improvementPlan.narrative}</p>
                                )}
                                {improvementPlan.leaderGuide && (
                                    <div className="coach-plan-guide mt-md">
                                        <p>
                                            <strong>Acción:</strong> {improvementPlan.leaderGuide.action}
                                        </p>
                                        <p>
                                            <strong>Pregunta:</strong> {improvementPlan.leaderGuide.question}
                                        </p>
                                        <p className="text-sm text-muted">{improvementPlan.leaderGuide.followUp}</p>
                                    </div>
                                )}
                            </div>
                            <div className="grid-2">
                                <div className="glass-card">
                                    <h4>Formación sugerida (4 semanas)</h4>
                                    <ul className="coach-list ml-md">
                                        {(improvementPlan.formationSuggestions || []).map((f, i) => (
                                            <li key={i}>{f}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="glass-card">
                                    <h4>Prioridades en la manada</h4>
                                    {(improvementPlan.priorities || []).length === 0 ? (
                                        <p className="text-muted text-sm">Sin señales ámbar/rojas prioritarias hoy.</p>
                                    ) : (
                                        <ul className="coach-list ml-md text-sm">
                                            {improvementPlan.priorities.map((p) => (
                                                <li key={p.workerId}>
                                                    {p.name} {frictionBadge(p.frictionLevel)}
                                                    <br />
                                                    <span className="text-muted">{p.suggestedAction}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === 'plan' && !tabLoading && !improvementPlan && (
                        <p className="text-muted">No hay plan disponible. Verifique encuestas del Observador y supervisores activos.</p>
                    )}

                    {tab === 'guides' && (
                        <div className="coach-content stagger">
                            <CoachManadaView
                                isAdmin={isAdmin}
                                isSupervisor={isSupervisor}
                                guides={guides}
                                leaders={guidesLeaders}
                                employees={manadaEmployees}
                                teamData={teamData}
                                leaderPlan={leaderPlanPreview}
                                loading={tabLoading && !guidesLoaded}
                                selectedLeaderId={selectedGuideLeaderId}
                                onSelectLeader={loadLeaderPlanPreview}
                                onOpenPlan={handleOpenFromManada}
                                loadingPlan={leaderPlanPreviewLoading}
                            />
                        </div>
                    )}

                    {tab === 'questions' && (
                        <div className="coach-content stagger">
                            {isAdmin ? (
                                <CoachQuestionsBank />
                            ) : (
                                questions && (
                                    <>
                                        <p className="text-muted mb-lg">
                                            Preguntas del banco maestro (solo lectura). El administrador puede editarlas.
                                        </p>
                                        <div className="grid-2">
                                            {Object.entries(questions).map(([key, section]) => (
                                                <div
                                                    key={key}
                                                    className="glass-card"
                                                    style={{ borderTop: `4px solid ${getThemeColorForKey(key)}` }}
                                                >
                                                    <h4>{COACH_QUESTION_CATEGORIES[key] || section.purpose}</h4>
                                                    <ul className="coach-list" style={{ listStyle: 'none', padding: 0 }}>
                                                        {section.questions.map((q, i) => (
                                                            <li key={i} style={{ marginBottom: '0.5rem', fontStyle: 'italic' }}>
                                                                "{q}"
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )
                            )}
                        </div>
                    )}
            </>
        </div>
    );
}

function getThemeColorForKey(key) {
    const map = {
        escuchar: 'var(--color-primary)',
        error: 'var(--color-alert-red)',
        adaptarse: 'var(--color-alert-yellow)',
        proposito: 'var(--color-alert-green)',
        constante: '#16a34a',
        impulsivo: '#dc2626',
        analitico: '#2563eb',
        social: '#ca8a04',
    };
    return map[key] || 'var(--color-text-muted)';
}
